import { isEngineElementType, type ElementType, type FormElement } from '@record-fill';
import { getElementRegistryEntry } from '@/features/FormBuilder';

// A missing or extra key fails to compile, so this record is the exhaustive list of the
// builder's element types, in the order the contract table shows them.
const ELEMENT_TYPE_ORDER: Record<ElementType, number> = {
  HEADER: 0,
  FIXED_TEXT: 1,
  TABLE: 2,
  TEXT_INPUT: 3,
  NUMBER_INPUT: 4,
  DROPDOWN: 5,
  CHECKBOX: 6,
  PARAGRAPH: 7,
  DATE_INPUT: 8,
  URL_INPUT: 9,
  EMAIL_INPUT: 10,
  REGISTER_NUMBER_INPUT: 11,
  INDEXER_TITLE: 12,
  FILL_TITLE: 13,
  IMAGE: 14,
  RADIO_GROUP: 15,
};

export const ELEMENT_TYPES = (Object.keys(ELEMENT_TYPE_ORDER) as ElementType[]).sort(
  (a, b) => ELEMENT_TYPE_ORDER[a] - ELEMENT_TYPE_ORDER[b],
);

export interface ElementContractRow {
  type: ElementType;
  label: string;
  hasEngine: boolean;
  count: number;
}

interface CellLike {
  content?: FormElement;
}

export function* walkElements(elements: FormElement[]): Generator<FormElement> {
  for (const element of elements) {
    yield element;
    if (element.type !== 'TABLE') continue;
    const cells = (element.properties as { cells?: CellLike[][] }).cells ?? [];
    for (const row of cells) {
      for (const cell of row) {
        if (cell.content) yield* walkElements([cell.content]);
      }
    }
  }
}

export function countElementTypes(elements: FormElement[]): Record<ElementType, number> {
  const counts = Object.fromEntries(ELEMENT_TYPES.map((type) => [type, 0])) as Record<ElementType, number>;
  for (const element of walkElements(elements)) counts[element.type] += 1;
  return counts;
}

export function sampleElementOfEachType(elements: FormElement[]): Partial<Record<ElementType, FormElement>> {
  const samples: Partial<Record<ElementType, FormElement>> = {};
  for (const element of walkElements(elements)) {
    if (samples[element.type]) continue;
    if (element.type === 'TABLE') {
      const { cells, ...rest } = element.properties as { cells?: CellLike[][] };
      const cellCount = (cells ?? []).reduce((total, row) => total + row.length, 0);
      samples[element.type] = {
        ...element,
        properties: { ...rest, cells: `[${cellCount} células omitidas]` } as unknown as FormElement['properties'],
      };
      continue;
    }
    samples[element.type] = element;
  }
  return samples;
}

export function buildElementContract(elements: FormElement[]): ElementContractRow[] {
  const counts = countElementTypes(elements);
  return ELEMENT_TYPES.map((type) => ({
    type,
    label: getElementRegistryEntry(type)?.label ?? type,
    hasEngine: isEngineElementType(type),
    count: counts[type],
  }));
}
